import React from "react";
import { PalpitesData } from "./palpitesGenerator";
import { animalMapping } from "@/utils/animalImages";
import { Calendar, Crown, Zap, Star, Youtube, MessageCircle } from "lucide-react";

interface Props {
  step: number; // 0..11
  data: PalpitesData;
}

const SlideShell: React.FC<{ children: React.ReactNode; title?: string; icon?: React.ReactNode }> = ({
  children,
  title,
  icon,
}) => (
  <div className="bg-gradient-to-br from-neutral-950 via-black to-neutral-900 rounded-3xl border-4 border-yellow-500/60 shadow-[0_0_60px_rgba(234,179,8,0.25)] p-8 md:p-12 min-h-[520px] flex flex-col">
    {title && (
      <div className="flex items-center justify-center gap-3 mb-6">
        {icon}
        <h2 className="text-4xl md:text-6xl font-black text-yellow-400 uppercase tracking-tight drop-shadow-[0_0_15px_rgba(234,179,8,0.5)] text-center">
          {title}
        </h2>
      </div>
    )}
    <div className="flex-1 flex items-center justify-center">{children}</div>
  </div>
);

const AnimalBadge: React.FC<{ grupo: string; size?: "sm" | "md" | "lg" }> = ({ grupo, size = "md" }) => {
  const info = animalMapping[grupo];
  if (!info) return null;
  const sizes = {
    sm: "w-16 h-16",
    md: "w-28 h-28",
    lg: "w-44 h-44",
  };
  return (
    <div className="flex flex-col items-center gap-2">
      <img
        src={info.image}
        alt={`Grupo ${grupo} - ${info.name}`}
        className={`${sizes[size]} rounded-full object-cover border-4 border-yellow-500 bg-white shadow-xl`}
      />
      <div className="text-yellow-300 font-bold uppercase text-xl md:text-2xl">
        {grupo} · {info.name}
      </div>
    </div>
  );
};


const SlideRenderer: React.FC<Props> = ({ step, data }) => {
  // Slide 0 — Capa
  if (step === 0) {
    return (
      <SlideShell>
        <div className="text-center space-y-6">
          <div className="flex justify-center gap-4 mb-4">
            {data.palpitesOuro.map((p, i) => (
              <AnimalBadge key={`${p.grupo}-${i}`} grupo={p.grupo} size="md" />
            ))}

          </div>
          <div className="text-3xl md:text-5xl font-black text-white uppercase">PALPITE DO</div>
          <div className="text-5xl md:text-8xl font-black bg-gradient-to-r from-yellow-400 to-amber-300 bg-clip-text text-transparent uppercase leading-none">
            I.A Palpite Guru
          </div>
          <div className="inline-flex items-center gap-3 bg-yellow-500 text-black px-6 py-3 rounded-2xl text-3xl md:text-4xl font-black shadow-2xl">
            <Calendar className="w-8 h-8" /> {data.data}
          </div>
        </div>
      </SlideShell>
    );
  }

  // Slide 1 — Teremos hoje
  if (step === 1) {
    const itens = [
      "🐾 BICHO DO DIA",
      "👑 4 PALPITES DE OURO COM MILHAR E CENTENA",
      "🔁 PASSE VAI E PASSE VEM",
      "🎯 DUQUES DE DEZENAS",
      "🎯 TERNO DE DEZENAS",
      "🔥 + BÔNUS EXTRA FORTE",
    ];
    return (
      <SlideShell title="Teremos Hoje" icon={<Star className="w-12 h-12 text-yellow-400" />}>
        <ul className="space-y-4 text-left">
          {itens.map((i) => (
            <li key={i} className="text-3xl md:text-5xl font-bold text-white">
              {i}
            </li>
          ))}
        </ul>
      </SlideShell>
    );
  }

  // Slide 2 — Bicho do dia
  if (step === 2) {
    return (
      <SlideShell title="Bicho do Dia" icon={<Zap className="w-12 h-12 text-yellow-400" />}>
        <div className="grid grid-cols-2 gap-8 md:gap-12">
          {data.bichoDoDia.map((m, i) => (
            <div key={i} className="text-center">
              <div className="text-sm md:text-xl text-yellow-300 font-bold mb-2">M.C</div>
              <div className="text-7xl md:text-9xl font-black text-white tracking-wider drop-shadow-[0_0_25px_rgba(234,179,8,0.7)]">
                {m}
              </div>
            </div>
          ))}
        </div>
      </SlideShell>
    );
  }

  // Slides 3..6 — Palpites de Ouro 1..4
  if (step >= 3 && step <= 6) {
    const idx = step - 3;
    const p = data.palpitesOuro[idx];
    return (
      <SlideShell title={`Palpite de Ouro ${idx + 1}`} icon={<Crown className="w-12 h-12 text-yellow-400" />}>
        <div className="w-full space-y-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            <AnimalBadge grupo={p.grupo} size="lg" />
            <div className="text-center md:text-left">
              <div className="text-2xl md:text-3xl text-yellow-300 font-bold">GRUPO {p.grupo}</div>
              <div className="text-6xl md:text-8xl font-black text-white leading-none">
                DZ {p.dezena}
              </div>
            </div>
          </div>
          <div>
            <div className="text-xl md:text-2xl text-yellow-400 font-bold mb-2 uppercase">Centenas</div>
            <div className="text-4xl md:text-6xl font-black text-white tracking-wider">
              {p.centenas.join(" · ")}
            </div>
          </div>
          <div>
            <div className="text-xl md:text-2xl text-yellow-400 font-bold mb-2 uppercase">Milhares</div>
            <div className="text-4xl md:text-6xl font-black text-white tracking-wider">
              {p.milhares.join(" · ")}
            </div>
          </div>
        </div>
      </SlideShell>
    );
  }

  // Slide 7 — Passe Vai e Vem
  if (step === 7) {
    return (
      <SlideShell title="Passe Vai e Vem" icon={<Zap className="w-12 h-12 text-yellow-400" />}>
        <div className="grid grid-cols-2 gap-6 md:gap-10">
          {data.passeVaiVem.map((p, i) => (
            <div key={i} className="text-center bg-yellow-500/10 border-2 border-yellow-500/40 rounded-2xl py-6 px-8">
              <div className="text-5xl md:text-7xl font-black text-white">
                G {p.g1} <span className="text-yellow-400">–</span> {p.g2}
              </div>
            </div>
          ))}
        </div>
      </SlideShell>
    );
  }

  // Slide 8 — Duques
  if (step === 8) {
    return (
      <SlideShell title="Duque de Dezenas de Ouro" icon={<Star className="w-12 h-12 text-yellow-400" />}>
        <div className="space-y-3">
          {data.duques.map((d, i) => (
            <div key={i} className="text-5xl md:text-7xl font-black text-white tracking-wider">
              {d.numeros.join(" - ")}
            </div>
          ))}
        </div>
      </SlideShell>
    );
  }

  // Slide 9 — Ternos
  if (step === 9) {
    return (
      <SlideShell title="Terno de Dezenas de Ouro" icon={<Star className="w-12 h-12 text-yellow-400" />}>
        <div className="space-y-3">
          {data.ternos.map((t, i) => (
            <div key={i} className="text-5xl md:text-7xl font-black text-white tracking-wider">
              {t.numeros.join(" - ")}
            </div>
          ))}
        </div>
      </SlideShell>
    );
  }

  // Slide 10 — Bônus + Forte
  if (step === 10) {
    return (
      <SlideShell title="Bônus + Forte" icon={<Crown className="w-12 h-12 text-yellow-400" />}>
        <div className="text-center space-y-6">
          <div>
            <div className="text-2xl md:text-3xl text-yellow-400 font-bold uppercase">Milhar</div>
            <div className="text-8xl md:text-[10rem] font-black text-white leading-none drop-shadow-[0_0_30px_rgba(234,179,8,0.8)]">
              {data.bonusMilhar}
            </div>
          </div>
          <div>
            <div className="text-2xl md:text-3xl text-yellow-400 font-bold uppercase">Centena</div>
            <div className="text-8xl md:text-[10rem] font-black text-white leading-none drop-shadow-[0_0_30px_rgba(234,179,8,0.8)]">
              {data.bonusCentena}
            </div>
          </div>
        </div>
      </SlideShell>
    );
  }

  // Slide 11 — CTA Final
  return (
    <SlideShell>
      <div className="text-center space-y-6 max-w-3xl">
        <div className="text-3xl md:text-5xl font-black text-yellow-400 uppercase">
          Deixe o Like e Se Inscreva no Canal!
        </div>
        <p className="text-2xl md:text-3xl text-white font-bold leading-tight">
          "A inteligência é a habilidade de aprender com a experiência."
        </p>
        <div className="bg-gradient-to-r from-yellow-500 to-amber-400 text-black rounded-2xl p-6 space-y-3">
          <div className="flex items-center justify-center gap-2 text-2xl md:text-3xl font-black">
            <MessageCircle className="w-8 h-8" /> NOVO: GURU CHAT
          </div>
          <p className="text-lg md:text-xl font-semibold">
            Converse com a nossa IA especialista em Jogo do Bicho — palpites personalizados,
            análise de resultados e estratégias exclusivas 24h no ar.
          </p>
        </div>
        <div className="flex items-center justify-center gap-3 text-2xl md:text-3xl font-black text-white">
          <Youtube className="w-8 h-8 text-red-500" /> Inscreva-se no Canal
        </div>
        <div className="text-xl md:text-2xl text-yellow-300 font-bold uppercase">
          🌐 aipalpiteguru.mentepoderosa2030.com
        </div>
      </div>
    </SlideShell>
  );
};

export const TOTAL_STEPS = 12;
export default SlideRenderer;
